/**
 * Shared merchant policy constants used by both the Google Shopping feed
 * (`src/utils/googleShoppingFeed.ts`) and the on-page Product JSON-LD
 * (`src/components/SEO.tsx`).
 *
 * Centralising these ensures Google sees a consistent return-policy and
 * payment-method story across every surface — feed XML, JSON-LD, Open
 * Graph, the public `/refund-policy` page, and Merchant Center custom
 * labels.
 */

/**
 * High-level return-policy category, aligned with Google Merchant Center's
 * three return-policy options. We currently accept returns only for
 * defective products — defect returns are governed by statutory consumer
 * protection law rather than a voluntary merchant return window.
 */
export const RETURN_POLICY_TYPE = "defective_only" as const;

/**
 * We do not offer exchanges. Defective items are refunded, not swapped.
 * Surfaced in listing copy and the public refund policy.
 */
export const EXCHANGES_ACCEPTED = false;

/**
 * Internal process window: defects must be reported within this many days
 * of delivery to be eligible for a refund. This is NOT a Google "return
 * window" (we do not accept customer-remorse returns), it only governs
 * the operational cutoff for defect claims on the public refund policy
 * page.
 */
export const DEFECT_REPORT_WINDOW_DAYS = 30;

/** ISO 3166 alpha-2 country the return policy applies to. */
export const RETURN_POLICY_COUNTRY = "US";

/** Public-facing URL where the full return/refund policy is described. */
export const RETURN_POLICY_URL_PATH = "/refund-policy";

/**
 * Schema.org `returnPolicyCategory` value that matches our Merchant Center
 * configuration ("Accept returns for defective products only").
 *
 * Schema.org exposes three enum values: FiniteReturnWindow,
 * UnlimitedWindow, and NotPermitted. "Defective only" is not a distinct
 * value — the closest honest mapping is `MerchantReturnNotPermitted`,
 * which correctly signals that the merchant does not accept voluntary
 * (customer-remorse) returns. Defect returns continue to be honored
 * separately under statutory rights, as described at the policy URL.
 */
export const SCHEMA_RETURN_POLICY_CATEGORY =
  "https://schema.org/MerchantReturnNotPermitted" as const;

/**
 * Digital wallets and payment methods accepted at checkout. Order matters —
 * first entries are surfaced first in the listing copy.
 */
export const ACCEPTED_PAYMENT_METHODS = [
  "Apple Pay",
  "Google Pay",
  "Card",
] as const;

export type AcceptedPaymentMethod = (typeof ACCEPTED_PAYMENT_METHODS)[number];

/**
 * Map our human-readable payment method labels to the canonical schema.org
 * `PaymentMethod` enumeration values used in `Offer.acceptedPaymentMethod`.
 *
 * Schema.org's PaymentMethod enumeration intentionally has no Apple Pay /
 * Google Pay members — those wallets ride on top of credit cards. So we
 * advertise wallet acceptance via a free-text `Offer.paymentAccepted`
 * field while keeping `acceptedPaymentMethod` to the strict enum.
 */
export const SCHEMA_ACCEPTED_PAYMENT_METHODS = [
  "https://schema.org/CreditCard",
  "https://schema.org/DebitCard",
] as const;
